import * as React from 'react';
import { useMergeRefs, FloatingPortal, FloatingArrow } from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import styled from 'styled-components';
import { Slot } from '@radix-ui/react-slot';
import useFloatingElement from '../utils/use-floating-element';
import { LabelRegular } from '../typography/typography';

const StyledTooltipContent = styled.div`
	background-color: var(--tooltip-bg-color);
`;

const TooltipLabel = styled(LabelRegular)`
	font-size: 12px;
	color: var(--tooltip-text-color);
`;

interface TooltipOptions {
	initialOpen?: boolean;
	placement?: Placement;
	isOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
}

type ContextType = ReturnType<typeof useFloatingElement> | null;

const TooltipContext = React.createContext<ContextType>(null);

const useTooltipContext = () => {
	const context = React.useContext(TooltipContext);

	if (context == null) {
		throw new Error('Tooltip components must be wrapped in <Tooltip />');
	}

	return context;
};

function Root({ children, ...options }: { children: React.ReactNode } & TooltipOptions) {
	const tooltip = useFloatingElement(options);
	return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

const Trigger = React.forwardRef<HTMLElement, React.HTMLProps<HTMLElement> & { asChild?: boolean }>(
	function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
		const context = useTooltipContext();
		const ref = useMergeRefs([context.refs.setReference, propRef]);

		if (asChild) {
			return (
				<Slot ref={ref} {...context.getReferenceProps(props)}>
					{children}
				</Slot>
			);
		}

		return (
			<button ref={ref} {...context.getReferenceProps(props)}>
				{children}
			</button>
		);
	}
);

const Content = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement> & { portalId?: string }>(
	function TooltipContent({ style, portalId, children, ...props }, propRef) {
		const context = useTooltipContext();
		const ref = useMergeRefs([context.refs.setFloating, propRef]);

		if (!context.isOpen) {
			return null;
		}

		return (
			<FloatingPortal id={portalId}>
				<StyledTooltipContent
					ref={ref}
					style={{
						...context.floatingStyles,
						...style
					}}
					{...context.getFloatingProps(props)}
				>
					<TooltipLabel>{children}</TooltipLabel>
				</StyledTooltipContent>
			</FloatingPortal>
		);
	}
);

const StyledArrow = styled(FloatingArrow)`
	fill: var(--floating-element-bg-color);

	& path:first-of-type {
		stroke: var(--floating-element-bg-color);
	}

	& path:last-of-type {
		stroke: var(--floating-element-bg-color);
	}
`;

type ArrowProps = React.HTMLProps<SVGSVGElement> & {
	staticOffset?: number;
	tipRadius?: number;
};

/**
 * @title Arrow
 * @description The PopoverArrow component is used to render the arrow of the Popover providing a visual connection between the Popover and the trigger element
 * @param tipRadius - The radius (rounding) of the arrow tip. Defaults to 0.
 * @param staticOffset - A static offset override of the arrow from the floating element edge. Often desirable if the floating element is smaller than the reference element along the relevant axis and has an edge alignment ('start'/'end').
 */
const Arrow = React.forwardRef<SVGSVGElement, ArrowProps>(({ className = '', staticOffset, tipRadius }, propRef) => {
	const { context, arrowRef } = useTooltipContext();
	const ref = useMergeRefs([propRef, arrowRef]);

	return (
		<StyledArrow
			ref={ref}
			context={context}
			className={className}
			tipRadius={tipRadius}
			staticOffset={staticOffset}
		/>
	);
});

Arrow.displayName = 'TooltipArrow';

export default {
	Root,
	Content,
	Trigger,
	Arrow
};
